import { useState } from "react";
import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { useContentPosts, useDeleteContentPost } from "../../../hooks/useContentManagement";
import type { ContentPostEntry } from "../../../server/content-management";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { toast } from "../../../components/ui/sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { queryKeys, STALE_TIMES } from "../../../lib/queryClient";
import { getContentPosts } from "../../../server/content-management";
import type { RouterContextWithSession } from "../../../router";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { LinkedInStudio } from "../../../components/admin/linkedin/LinkedInStudio";
import { OmniStudio } from "../../../components/admin/content/OmniStudio";
import { ContentManagementSettings } from "../../../components/admin/content/ContentManagementSettings";
import { Linkedin, FileText, Globe2, Settings } from "lucide-react";


export const Route = createFileRoute("/_admin/content-management/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;

    await context.queryClient.ensureQueryData({
      queryKey: queryKeys.contentManagement.posts(1, 10),
      queryFn: async () => {
        const result = await getContentPosts({
          data: { accessToken: session.access_token, page: 1, limit: 10 },
        });
        if (result.error) throw new Error(result.error);
        return result.data;
      },
      staleTime: STALE_TIMES.system,
    });
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="list" />,
  errorComponent: createRouteError("Content Management"),
  component: ContentManagementPage,
});

function ContentManagementPage() {
  const { session, loading, isAuthenticated } = useAuth();

  if (loading || !isAuthenticated || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return <AuthenticatedContentManagement accessToken={session.access_token} />;
}

function AuthenticatedContentManagement({ accessToken }: { accessToken: string }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const limit = 10;

  const { data, isLoading, error } = useContentPosts(
    { accessToken },
    { page, limit, search: search || undefined }
  );

  const deleteMutation = useDeleteContentPost({ accessToken });

  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleDelete = (postId: string) => {
    setConfirmDeleteId(postId);
  };

  return (
    <div className="text-foreground">
      <div className="container mx-auto px-4 py-6 max-w-5xl space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Content & Marketing Hub</h1>
            <p className="text-muted-foreground">
              Automate LinkedIn content, profile funnels, and manage community posts.
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/dashboard">Back to Dashboard</Link>
          </Button>
        </header>

        <Tabs defaultValue="omni" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 max-w-2xl bg-muted/60 p-1">
            <TabsTrigger value="omni" className="gap-2 font-medium">
              <Globe2 className="h-4 w-4 text-primary" />
              Omni-Channel Studio
            </TabsTrigger>
            <TabsTrigger value="linkedin" className="gap-2 font-medium">
              <Linkedin className="h-4 w-4 text-blue-600" />
              LinkedIn Deep Studio
            </TabsTrigger>
            <TabsTrigger value="community" className="gap-2 font-medium">
              <FileText className="h-4 w-4 text-muted-foreground" />
              Community Posts
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2 font-medium">
              <Settings className="h-4 w-4 text-muted-foreground" />
              Compliance & Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="omni" className="m-0">
            <OmniStudio />
          </TabsContent>

          <TabsContent value="linkedin" className="m-0">
            <LinkedInStudio />
          </TabsContent>

          <TabsContent value="settings" className="m-0">
            <ContentManagementSettings />
          </TabsContent>

          <TabsContent value="community" className="space-y-6 m-0">
            <Card>
              <CardHeader>
                <CardTitle>Community Posts</CardTitle>
                <CardDescription>
                  {data ? `${data.total} posts` : "Loading..."}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Search posts by title..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    aria-label="Search posts"
                  />
                  <Button variant="outline" size="sm" onClick={handleSearch}>
                    Search
                  </Button>
                </div>

                {isLoading && (
                  <p className="text-muted-foreground">Loading posts...</p>
                )}
                {error && (
                  <p className="text-destructive">Error: {error.message}</p>
                )}
                {!isLoading && !error && (!data || data.posts.length === 0) && (
                  <p className="text-muted-foreground">No posts found.</p>
                )}
                {!isLoading && !error && data && data.posts.length > 0 && (
                  <div className="space-y-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-3 font-medium text-muted-foreground">Title</th>
                            <th className="text-left py-2 px-3 font-medium text-muted-foreground">Author</th>
                            <th className="text-left py-2 px-3 font-medium text-muted-foreground">Created</th>
                            <th className="text-right py-2 px-3 font-medium text-muted-foreground">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.posts.map((post) => (
                            <tr key={post.id} className="border-b last:border-0 hover:bg-muted/50">
                              <td className="py-2.5 px-3 font-medium">{post.title}</td>
                              <td className="py-2.5 px-3 text-muted-foreground">
                                {post.author_name || post.author_username || "Unknown"}
                              </td>
                              <td className="py-2.5 px-3 text-muted-foreground">
                                {new Date(post.created_at).toLocaleDateString()}
                              </td>
                              <td className="py-2.5 px-3 text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  disabled={deleting === post.id}
                                  onClick={() => handleDelete(post.id)}
                                >
                                  {deleting === post.id ? "Deleting..." : "Delete"}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pt-2">
                        <p className="text-xs text-muted-foreground">
                          Page {page} of {totalPages}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            Previous
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => p + 1)}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={confirmDeleteId !== null} onOpenChange={(open) => { if (!open) setConfirmDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The post will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmDeleteId(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!confirmDeleteId) return;
                setDeleting(confirmDeleteId);
                try {
                  await deleteMutation.mutateAsync(confirmDeleteId);
                  toast.success("Post deleted");
                } catch {
                  toast.error("Failed to delete post");
                } finally {
                  setDeleting(null);
                  setConfirmDeleteId(null);
                }
              }}
            >
              Delete Post
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
